import { get, post, put, del } from '@/lib/api-client';

// 仓库相关类型
export interface Warehouse {
  id: string;
  code: string;
  nameEn: string;
  nameCn?: string;
  description?: string;
  address?: string;
  contactPerson?: string;
  contactPhone?: string;
  status: 'ACTIVE' | 'INACTIVE' | 'MAINTENANCE';
  isDefault: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  _count?: {
    storageLocations: number;
    parts: number;
  };
}

export interface StorageLocation {
  id: string;
  warehouseId: string;
  code: string;
  nameEn: string;
  nameCn?: string;
  description?: string;
  zone?: string;
  aisle?: string;
  rack?: string;
  level?: string;
  position?: string;
  capacity?: number;
  status: 'ACTIVE' | 'INACTIVE' | 'FULL' | 'RESERVED';
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  warehouse?: {
    id: string;
    code: string;
    nameEn: string;
    nameCn?: string;
  };
  _count?: {
    parts: number;
  };
}

export interface CreateWarehouseDto {
  code: string;
  nameEn: string;
  nameCn?: string;
  description?: string;
  address?: string;
  contactPerson?: string;
  contactPhone?: string;
  isDefault?: boolean;
  sortOrder?: number;
}

export interface UpdateWarehouseDto {
  code?: string;
  nameEn?: string;
  nameCn?: string;
  description?: string;
  address?: string;
  contactPerson?: string;
  contactPhone?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'MAINTENANCE';
  isDefault?: boolean;
  sortOrder?: number;
}

export interface CreateStorageLocationDto {
  warehouseId: string;
  code: string;
  nameEn: string;
  nameCn?: string;
  description?: string;
  zone?: string;
  aisle?: string;
  rack?: string;
  level?: string;
  position?: string;
  capacity?: number;
  sortOrder?: number;
}

export interface UpdateStorageLocationDto {
  code?: string;
  nameEn?: string;
  nameCn?: string;
  description?: string;
  zone?: string;
  aisle?: string;
  rack?: string;
  level?: string;
  position?: string;
  capacity?: number;
  status?: 'ACTIVE' | 'INACTIVE' | 'FULL' | 'RESERVED';
  sortOrder?: number;
}

export interface QueryWarehouseParams {
  search?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'MAINTENANCE';
  page?: number;
  limit?: number;
}

export interface QueryStorageLocationParams {
  warehouseId?: string;
  search?: string;
  zone?: string;
  status?: 'ACTIVE' | 'INACTIVE' | 'FULL' | 'RESERVED';
  page?: number;
  limit?: number;
}

// PaginatedResponse 从 index.ts 导出，这里不重复定义
interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// 仓库 API
export const warehouseApi = {
  // 获取仓库列表
  list: (params?: QueryWarehouseParams): Promise<PaginatedResponse<Warehouse>> =>
    get('/warehouses', { params }),

  // 获取仓库选项（下拉框）
  getSelectOptions: (): Promise<Pick<Warehouse, 'id' | 'code' | 'nameEn' | 'nameCn' | 'isDefault'>[]> =>
    get('/warehouses/select'),

  // 获取单个仓库
  get: (id: string): Promise<Warehouse & { storageLocations: StorageLocation[] }> =>
    get(`/warehouses/${id}`),

  // 创建仓库
  create: (data: CreateWarehouseDto): Promise<Warehouse> =>
    post('/warehouses', data),

  // 更新仓库
  update: (id: string, data: UpdateWarehouseDto): Promise<Warehouse> =>
    put(`/warehouses/${id}`, data),

  // 删除仓库
  delete: (id: string): Promise<void> =>
    del(`/warehouses/${id}`),
};

// 仓位 API
export const storageLocationApi = {
  // 获取仓位列表
  list: (params?: QueryStorageLocationParams): Promise<PaginatedResponse<StorageLocation>> =>
    get('/storage-locations', { params }),

  // 获取仓库下的仓位（下拉框）
  getByWarehouse: (warehouseId: string): Promise<Pick<StorageLocation, 'id' | 'code' | 'nameEn' | 'nameCn' | 'zone'>[]> =>
    get(`/storage-locations/by-warehouse/${warehouseId}`),

  // 获取所有区域
  getZones: (warehouseId?: string): Promise<string[]> =>
    get('/storage-locations/zones', { params: warehouseId ? { warehouseId } : undefined }),

  // 获取单个仓位
  get: (id: string): Promise<StorageLocation> =>
    get(`/storage-locations/${id}`),

  // 创建仓位
  create: (data: CreateStorageLocationDto): Promise<StorageLocation> =>
    post('/storage-locations', data),

  // 更新仓位
  update: (id: string, data: UpdateStorageLocationDto): Promise<StorageLocation> =>
    put(`/storage-locations/${id}`, data),

  // 删除仓位
  delete: (id: string): Promise<void> =>
    del(`/storage-locations/${id}`),

  // 获取导入模板
  getImportTemplate: (): Promise<any[]> =>
    get('/storage-locations/import-template'),

  // 批量导入仓位
  bulkImport: (warehouseId: string, locations: Omit<CreateStorageLocationDto, 'warehouseId'>[]): Promise<{
    success: number;
    failed: number;
    errors: { row: number; code: string; error: string }[];
    created: StorageLocation[];
  }> => post(`/storage-locations/bulk-import/${warehouseId}`, { locations }),
};

